import { WarrantySelectCoverageLayout } from "@modules/cms/types/WarrantySelectCoverageLayout";
import { GetBackgroundStyle } from "@talladega/helpers";
import { useTranslation } from "next-i18next";
import LocalImage from "@common/components/LocalImage";
import {
  WarrantyHeroEn,
  WarrantyHeroFr,
} from "@public/images/purchase/coverage";
import { ImageWithTranslation } from "@common/types/ImageWithTranslation";
import { useRouter } from "next/router";
import { useState } from "react";
import IALogoWhite from "@public/images/ia-logo-white.svg";
import KijijiLogoWhite from "@public/images/kijiji-logo-white.svg";
import AutoCanLogoWhite from "@public/images/autocan-logo-white.svg";
import {
  quoteVehicleFormMode,
  WarrantySegmentationMode,
} from "../types/WarrantySegmentationMode";
import WarrantySegmentationForm from "./WarrantySegmentationForm";

const vehicleDefaultImage: ImageWithTranslation = {
  srcEn: WarrantyHeroEn,
  srcFr: WarrantyHeroFr,
  altKey: "purchase:alt_wty_vehicle_img",
};

type Props = {
  layout: WarrantySelectCoverageLayout;
};

const WarrantySegmentationSection = ({ layout: { background } }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const router = useRouter();
  const { locale: rlocale } = router;
  const locale = rlocale || "en";

  const [warrantySegmentModeState, setWarrantySegmentModeState] =
    useState<WarrantySegmentationMode>(quoteVehicleFormMode);

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    <div
      className="bg-white overflow-visible no-scrollbar relative bg-cover bg-center"
      style={GetBackgroundStyle(background, baseDomain)}
    >
      <div className="relative mx-auto flex max-w-page flex-col items-center gap-4 px-4 pb-6 pt-10 lg:flex-row lg:px-5 lg:py-16">
        <div className="flex flex-col lg:basis-[55%]">
          <div className="flex-1 md:px-28 lg:basis-[40%] lg:px-0">
            <div className="w-full items-start justify-start text-left">
              <h1 className="text-4xl leading-[2.625rem] text-white md:text-5xl md:leading-[3.625rem]">
                {t("common:enjoy_your_ride_piece_of_mind")}
              </h1>
            </div>

            <div className="mt-4 flex w-full flex-col gap-4 lg:mt-6 items-start justify-start text-left">
              <p className="text-gray-100 text-base font-medium md:text-lg">
                {t("common:were_here_to_ensure_youre_covered")}
              </p>
            </div>

            {/** Brands Logo Section */}
            <div className="flex flex-wrap sm:flex-row sm:items-center mt-4 lg:mb-[8px] w-full">
              <span className="text-sm font-semibold text-gray-50 w-full sm:w-auto mb-2 sm:mb-0 sm:mr-1">
                {t("common:in_association_with")}
              </span>

              <div className="flex flex-wrap justify-start items-center w-full sm:w-auto">
                <LocalImage
                  className="h-auto w-12 lg:w-[75px] flex-none mb-[1px] px-2 lg:mb-[-5px]"
                  src={KijijiLogoWhite}
                  alt="Kijiji Logo"
                  priority
                />
                <hr className="border-l border-gray-200 h-[25px]" />

                <LocalImage
                  className="h-auto w-12 lg:w-[75px] flex-none mb-[1px] px-2 lg:mb-[-5px]"
                  src={IALogoWhite}
                  alt="IA Logo"
                  priority
                />
                <hr className="border-l border-gray-200 h-[25px] mx-3" />

                <LocalImage
                  className="h-auto w-28 lg:w-[120px] flex-none mb-[1px] lg:mb-[-5px]"
                  src={AutoCanLogoWhite}
                  alt="Autocan Logo"
                  priority
                />
              </div>
            </div>
          </div>
          <div className="relative w-full flex-1 mt-6">
            <div className="relative inset-0 h-auto min-w-full">
              <div className="relative h-56 overflow-auto md:h-96 lg:mr-4">
                <LocalImage
                  className="z-[2] h-auto w-full rounded-lg object-contain"
                  src={
                    locale === "fr"
                      ? vehicleDefaultImage.srcFr
                      : vehicleDefaultImage.srcEn
                  }
                  alt={t(vehicleDefaultImage.altKey)}
                  fill
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 basis-[53%] rounded-2xl bg-white px-6 py-12">
          <WarrantySegmentationForm
            modeState={[warrantySegmentModeState, setWarrantySegmentModeState]}
          />
        </div>
      </div>
    </div>
  );
};

export default WarrantySegmentationSection;
